/** Port of ActivityDetailsScreenMapper.kt.
 *
 * Deliberately isomorphic to the Kotlin: same emission order, same gating conditionals,
 * same names, no cleverness. When the upstream mapper changes, `renderer:drift` names
 * this file and the two diffs should line up statement for statement.
 *
 * Upstream: ui/my_trips_details/activity_details/ActivityDetailsScreenMapper.kt
 * Spec: aidocs/context/android-renderer-spec.md §1B
 */
import type { ActivityDomain } from '../adapters/activity';
import {
  bottomBarUnit,
  mapActivityPrice,
  mapDuration,
  type Currency,
} from '../text/format';

export const STRINGS = {
  information: 'Information',
  recommended: 'Recommended Nearby',
  hours: 'Hours of operation',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  website: 'Website',
  directions: 'Directions',
  reviews: (n: number) => `${n} ${n === 1 ? 'Review' : 'Reviews'}`,
} as const;

export type SliderImage = {
  key: string;
  image: { prefix: string; suffix: string } | null;
  /** the play badge — only when the image has a video AND we are "online" */
  overlayPlay: boolean;
  /** no images at all: one "coming soon" placeholder */
  placeholder?: boolean;
};

export type Item =
  | { kind: 'imageSlider'; key: string; images: SliderImage[] }
  | {
      kind: 'detailsHeader';
      key: string;
      topLabel: string | null;
      title: string;
      descriptionHtml: string | null;
      /** ActivityDetails hard-codes buttonText=null, expanded=true — no read-more here. */
      buttonText: string | null;
      expanded: boolean;
    }
  | { kind: 'ratingBar'; key: string; rating: number; text: string; url: string | null }
  | {
      kind: 'row';
      key: string;
      text: string;
      startIcon: 'document' | 'audio' | 'image' | 'link' | null;
      endIcon: 'chevron' | 'play';
      showDivider: boolean;
      target: string | null;
    }
  | { kind: 'section'; key: string; text: string }
  | { kind: 'rowDetails'; key: string; label: string; text: string; showDivider: boolean };

export interface BottomBar {
  title: string | null;
  caption: string | null;
  buttonText: string | null;
  /** end when there is price content, centre when there is not */
  buttonPlacement: 'end' | 'center';
}

export interface ActivityUiState {
  title: string;
  items: Item[];
  bottomModel: BottomBar | null;
  /** Things the payload genuinely cannot express — surfaced, not hidden. */
  notes: string[];
}

/** `ContentTypeResolver` picks the start icon and whether the end control is a player. */
function contentTypeOf(doc: { contentType: string | null; link: string | null }) {
  const ct = (doc.contentType ?? '').toLowerCase();
  if (ct.startsWith('audio/')) return 'audio' as const;
  if (ct.startsWith('image/')) return 'image' as const;
  if (ct === 'application/pdf') return 'document' as const;
  return 'link' as const;
}

export function mapActivityDetails(
  activity: ActivityDomain,
  currency: Currency | null,
  online = true,
): ActivityUiState {
  const items: Item[] = [];
  const notes: string[] = [];

  // 1 — image slider, always
  const sliderImages: SliderImage[] = [...activity.images]
    .sort((a, b) => a.sequence - b.sequence)
    .map((image) => ({
      key: String(image.id),
      image: image.image,
      overlayPlay: image.hasVideo && online,
    }));
  items.push({
    kind: 'imageSlider',
    key: 'image_slider',
    images:
      sliderImages.length > 0
        ? sliderImages
        : [{ key: 'placeholder', image: null, overlayPlay: false, placeholder: true }],
  });

  // 2 — details header, always. buttonText/expanded are hard-coded upstream.
  items.push({
    kind: 'detailsHeader',
    key: 'details_header',
    topLabel: mapDuration(activity.minDuration, activity.maxDuration),
    title: activity.name,
    descriptionHtml: activity.description,
    buttonText: null,
    expanded: true,
  });

  // 3 — rating
  if (activity.rating) {
    items.push({
      kind: 'ratingBar',
      key: 'rating',
      rating: activity.rating.starRating,
      text: STRINGS.reviews(activity.rating.reviewCount),
      url: activity.rating.reviewSourceUrl,
    });
  }

  // 4 — documents, by sequence
  for (const doc of [...activity.documents].sort((a, b) => a.sequence - b.sequence)) {
    const kind = contentTypeOf(doc);
    const isAudio = kind === 'audio';
    items.push({
      kind: 'row',
      key: `doc_${doc.id}`,
      text: doc.name,
      startIcon: kind,
      endIcon: isAudio ? 'play' : 'chevron',
      showDivider: true,
      target: doc.link,
    });
  }

  // 5 — information, in this fixed order, each skipped when blank
  const information: Item[] = [];
  if (activity.openingHours.length > 0) {
    information.push({
      kind: 'rowDetails',
      key: 'opening_hours',
      label: STRINGS.hours,
      // upstream sets Text.EMPTY here: visible, and blank
      text: '',
      showDivider: true,
    });
  }
  // mapActivityLevel() hard-returns null upstream — intentionally not ported.
  if (activity.email) {
    information.push({ kind: 'rowDetails', key: 'email', label: STRINGS.email, text: activity.email, showDivider: true });
  }
  if (activity.phone) {
    information.push({ kind: 'rowDetails', key: 'phone', label: STRINGS.phone, text: activity.phone, showDivider: true });
  }
  if (activity.address) {
    information.push({ kind: 'rowDetails', key: 'address', label: STRINGS.address, text: activity.address, showDivider: true });
  }
  if (activity.website) {
    information.push({ kind: 'rowDetails', key: 'website', label: STRINGS.website, text: activity.website, showDivider: true });
  }
  if (information.length > 0) {
    items.push({ kind: 'section', key: 'information', text: STRINGS.information });
    items.push(...information);
  }

  // 6 — Recommended Nearby.
  // The app fills these from its local DB; the payload carries child IDs only
  // (ActivitySerializer.get_children). There is nothing to render from one activity's
  // payload, so the section is omitted and the gap is stated rather than faked.
  if (activity.childIds.length > 0) {
    notes.push(
      `${activity.childIds.length} nearby ${activity.childIds.length === 1 ? 'activity is' : 'activities are'} ` +
        `linked. The app resolves these from its local catalog; the payload carries only ids, ` +
        `so the "Recommended Nearby" section cannot be previewed.`,
    );
  }

  // bottom bar
  const priceInput = {
    paymentType: activity.paymentType,
    priceType: activity.priceType,
    priceLevel: activity.priceLevel,
    costMin: activity.costMin,
    costMax: activity.costMax,
    costPrefix: activity.costPrefix,
    costUnit: activity.costUnit,
    currency,
  };
  const price = mapActivityPrice(priceInput);
  const unit = bottomBarUnit(priceInput, price);
  const hasContent = price !== null || unit !== null;
  const buttonText = activity.coordinates ? STRINGS.directions : null;

  const bottomModel: BottomBar | null =
    hasContent || buttonText
      ? {
          title: price,
          caption: unit,
          buttonText,
          buttonPlacement: hasContent ? 'end' : 'center',
        }
      : null;

  if (currency === null && price !== null && price.length === 0) {
    notes.push('The payload carries a currency id but no currency table, so the price symbol cannot be resolved.');
  }

  return { title: activity.name, items, bottomModel, notes };
}
